'use client';

import { getCopy } from '@/lib/copy';
import { apiClient } from '@/lib/api-client';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

const c = (key: string) => getCopy('/app/reconsent', key);

/**
 * Routes where the gate must NOT render, so the user can actually read the
 * new Terms / Privacy Policy and sign out if they decline. `/api/*` runs on
 * the server so it never reaches this component, but we exempt it in the
 * check just in case future API route handlers render React.
 */
const EXEMPT_PREFIXES = ['/auth', '/terms', '/privacy', '/api'];

function isExemptPath(pathname: string): boolean {
  if (!pathname) return true;
  if (pathname === '/') return true;
  return EXEMPT_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

type Status = {
  needsReconsent: boolean
  currentTermsVersion: string
  currentPrivacyVersion: string
  acceptedTermsVersion: string | null
  acceptedPrivacyVersion: string | null
  acceptedAt: string | null
};

/**
 * TermsReconsentGate
 *
 * Layout-level component that prompts the signed-in user to re-accept the
 * Terms of Service and Privacy Policy whenever the canonical versions in
 * `@purebox/shared` have been bumped since their last recorded assent.
 *
 * Behavior:
 *  - Renders nothing on exempt routes (`/auth/*`, `/terms`, `/privacy`,
 *    `/api/*`, landing `/`).
 *  - Calls `GET /v1/auth/terms-acceptance-status` once per route change when
 *    a Supabase session exists. If the endpoint is unreachable we fail
 *    closed on subsequent `POST` (never fail-open silently).
 *  - Renders a blocking modal with two choices: "I agree and continue"
 *    (records a new acceptance row with `method: 'reconsent', clickwrap:
 *    true`) or "Sign out".
 *  - The modal is intentionally not dismissable by ESC or backdrop click.
 */
export function TermsReconsentGate() {
  const pathname = usePathname() ?? '';
  const router = useRouter();
  const [status, setStatus] = useState<Status | null>(null);
  const [hasSession, setHasSession] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track whether we have a live Supabase session before calling the status
  // endpoint. Without a session the endpoint would always 401 and we'd show
  // no modal - which is the intended behavior, but we avoid the wasted call.
  useEffect(() => {
    let cancelled = false;
    let supabase: ReturnType<typeof createClient> | null = null;
    try {
      supabase = createClient();
    } catch {
      setHasSession(false);
      return;
    }

    async function checkSession() {
      try {
        const { data } = await supabase!.auth.getSession();
        if (!cancelled) setHasSession(Boolean(data.session));
      } catch {
        if (!cancelled) setHasSession(false);
      }
    }

    void checkSession();
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!cancelled) setHasSession(Boolean(session));
    });
    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!hasSession) return;
    if (isExemptPath(pathname)) return;
    let cancelled = false;

    async function loadStatus() {
      try {
        const s = await apiClient.auth.getTermsAcceptanceStatus();
        if (!cancelled) setStatus(s as Status);
      } catch {
        // Soft-fail: if we can't reach the status endpoint we don't render
        // the modal. The backend will still refuse any subsequent write
        // that depends on consent. A follow-up navigation will retry.
        if (!cancelled) setStatus(null);
      }
    }

    void loadStatus();
    return () => {
      cancelled = true;
    };
  }, [hasSession, pathname]);

  const handleAccept = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await apiClient.auth.recordTermsAcceptance({ method: 'reconsent', clickwrap: true });
      setStatus((prev) =>
        prev
          ? {
              ...prev,
              needsReconsent: false,
              acceptedTermsVersion: prev.currentTermsVersion,
              acceptedPrivacyVersion: prev.currentPrivacyVersion,
              acceptedAt: new Date().toISOString(),
            }
          : prev,
      );
      router.refresh();
    } catch {
      setError(c('reconsent.error'));
    } finally {
      setIsSubmitting(false);
    }
  }, [router]);

  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true);
    try {
      const supabase = createClient();
      await supabase.auth.signOut();
    } finally {
      router.push('/auth/login');
    }
  }, [router]);

  if (!hasSession) return null;
  if (isExemptPath(pathname)) return null;
  if (!status || !status.needsReconsent) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reconsent-title"
      aria-describedby="reconsent-body"
      data-testid="reconsent-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#191c1e]/60 px-4 font-[family-name:var(--font-manrope)]"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-[0px_16px_48px_rgba(25,28,30,0.16)]">
        <h2
          id="reconsent-title"
          className="mb-3 text-xl font-extrabold tracking-tight text-[#191c1e] md:text-2xl"
        >
          {c('reconsent.title')}
        </h2>
        <p
          id="reconsent-body"
          className="mb-5 text-sm leading-relaxed text-[#434655]"
        >
          {c('reconsent.body')}
        </p>
        <ul className="mb-6 space-y-2 text-sm">
          <li>
            <Link
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[#004ac6] underline decoration-[#004ac6]/40 underline-offset-2 hover:decoration-[#004ac6]"
            >
              {c('reconsent.termsLinkLabel')}
            </Link>
          </li>
          <li>
            <Link
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[#004ac6] underline decoration-[#004ac6]/40 underline-offset-2 hover:decoration-[#004ac6]"
            >
              {c('reconsent.privacyLinkLabel')}
            </Link>
          </li>
        </ul>

        {error && (
          <p
            role="alert"
            aria-live="assertive"
            data-testid="reconsent-error"
            className="mb-4 text-sm font-medium text-[#ba1a1a]"
          >
            {error}
          </p>
        )}

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleAccept}
            disabled={isSubmitting || isSigningOut}
            data-testid="reconsent-accept"
            className="w-full rounded-lg bg-gradient-to-br from-[#004ac6] to-[#2563eb] py-3 px-4 font-bold text-white shadow-md transition-all hover:opacity-95 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? c('reconsent.primaryCtaLoading') : c('reconsent.primaryCta')}
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={isSubmitting || isSigningOut}
            data-testid="reconsent-signout"
            className="w-full rounded-lg bg-[#e6e8ea] py-3 px-4 font-semibold text-[#54647a] transition-colors hover:bg-[#e0e3e5] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {c('reconsent.secondaryCta')}
          </button>
        </div>
      </div>
    </div>
  );
}
